"use client";

import type { AcademyCategory } from "@nyntax/contracts/schemas/course";
import { Button } from "@nyntax/design-system/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@nyntax/design-system/components/ui/dropdown-menu";
import Link from "next/link";
import { LuChevronDown, LuLayoutGrid } from "react-icons/lu";

import { CATEGORY_ICONS } from "@/components/catalog/category-nav";
import { catalogPath } from "@/lib/catalog-query";

interface HeaderExploreProps {
  categories: { label: string; value: AcademyCategory }[];
  labels: { all: string; explore: string; heading: string };
  locale: string;
}

/**
 * "Explore" in the header: every category with its icon, one click into the
 * catalog filtered by it, plus all courses.
 *
 * @param props - The categories, the words and the locale.
 * @returns The menu button and its list.
 */
export const HeaderExplore = ({ categories, labels, locale }: HeaderExploreProps) => (
  <DropdownMenu>
    <DropdownMenuTrigger render={<Button size="lg" variant="ghost" />}>
      <LuLayoutGrid aria-hidden="true" />
      {labels.explore}
      <LuChevronDown aria-hidden="true" className="opacity-60" />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="w-72">
      <DropdownMenuGroup>
        <DropdownMenuLabel>{labels.heading}</DropdownMenuLabel>
        {categories.map(({ label, value }) => {
          const Icon = CATEGORY_ICONS[value];

          return (
            <DropdownMenuItem key={value} render={<Link href={catalogPath(locale, { category: value })} />}>
              <span className="bg-primary/10 text-primary grid size-7 place-items-center rounded-md">
                <Icon aria-hidden="true" className="size-4" />
              </span>
              {label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuItem render={<Link href={`/${locale}`} />}>
        <LuLayoutGrid aria-hidden="true" />
        {labels.all}
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);
